/* eslint-disable @typescript-eslint/naming-convention */
// deps
import React, { useContext, useCallback, useMemo } from "react";

// internal-deps
import {
    SavingsLineItemData,
    SavingsRightSectionType,
    CouponlessSavingsWidgetInfo,
    RenderingDetailsMeta,
} from "@swiggy-private/cart-types";
import { CouponlessSavingsTimer, getHourMinSecFromDuration, getTimeInFormat } from "@swiggy-private/cart-ui";
import { format, isEmpty } from "@swiggy-private/js-utils";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

// helpers
import { getCouponlessSavingWidgetData } from "@cart-client/helpers/Coupon";

// analytics
import { APPLY_COUPON_TEXTS } from "@cart-client/constants/Coupon";

// constants
import { CART_V2_APPLY_COUPON_ICON, DEAL_RUSH_KEY } from "@cart-client/constants/index";

export interface UseCouponlessSavingsReturnType {
    getCouponLessSavingsItem: (icon: string, meta?: RenderingDetailsMeta) => SavingsLineItemData | null;
}

export const useCouponlessSavingsData = (
    onSavingsLineItemLoad: (title?: string, subtitle?: string) => void,
): UseCouponlessSavingsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;

    const { reloadCart } = useContext(UpdateCartContext);

    const rootCouponlessSavingsData = useMemo(() => getCouponlessSavingWidgetData(cartResponse), [cartResponse]);

    const getCouponLessSavingsItem = useCallback(
        (icon: string, meta?: RenderingDetailsMeta): SavingsLineItemData | null => {
            // Prioritize embedded couponless_savings_widget_info from rendering_details.meta
            // Fall back to root-level data if embedded data is missing or empty
            const couponlessSavingWidgetData = (
                !isEmpty(meta?.couponless_savings_widget_info)
                    ? meta?.couponless_savings_widget_info
                    : rootCouponlessSavingsData
            ) as CouponlessSavingsWidgetInfo | undefined;

            if (isEmpty(couponlessSavingWidgetData)) {
                return null;
            }
            let staticTitle = APPLY_COUPON_TEXTS.X_SAVED_WITH_BENEFIT;

            const benefitCallout = couponlessSavingWidgetData.benefit_callout.toUpperCase();
            // Check if benefit_callout contains DEALRUSH to use "including" instead of "with" in the title
            // Backend alignment: https://swiggy.slack.com/archives/C09LMKM9N0G/p1763453455556479?thread_ts=1763123348.050619&cid=C09LMKM9N0G
            if (benefitCallout.includes(DEAL_RUSH_KEY)) {
                staticTitle = APPLY_COUPON_TEXTS.X_SAVED_INCLUDING_BENEFIT;
            }
            const title = {
                value: format(
                    staticTitle,
                    couponlessSavingWidgetData.savings,
                    couponlessSavingWidgetData.benefit_callout,
                ),
            };

            let subtitle: SavingsLineItemData["subtitle"];
            let subtitleText: string;

            if (!isEmpty(couponlessSavingWidgetData.expiry_time)) {
                subtitle = {
                    subtitleNode: (
                        <CouponlessSavingsTimer
                            durationInSeconds={couponlessSavingWidgetData.expiry_time}
                            onComplete={reloadCart}
                        />
                    ),
                };

                const displayTime = getHourMinSecFromDuration(couponlessSavingWidgetData.expiry_time);
                subtitleText = format(APPLY_COUPON_TEXTS.ENDS_IN, getTimeInFormat(displayTime));
            }

            const rightSection = {
                type: SavingsRightSectionType.LABEL,
                value: APPLY_COUPON_TEXTS.APPLIED,
            };

            return {
                icon: icon || CART_V2_APPLY_COUPON_ICON,
                title,
                subtitle,
                rightSection,
                onLoad: () => {
                    onSavingsLineItemLoad(title.value, subtitleText);
                },
            };
        },
        [rootCouponlessSavingsData, onSavingsLineItemLoad, reloadCart],
    );

    return { getCouponLessSavingsItem };
};
